import { useEffect, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ErrorIcon from '@mui/icons-material/Error'
import Search from '@mui/icons-material/Search'
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Container,
	IconButton,
	InputAdornment,
	MenuItem,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

import { MODAL_TYPES, useModalContext } from 'contexts/ModalProvider'

import SubPageHeader from 'components/SubPageHeader'

import { styles } from './Admin.styles'
import { UserRow, adminDummyData } from './AdminDummyData'

export const TEST_ID = 'admin-page'

const USER_ROLES = ['Participant', 'Facilitator', 'Admin'] as const

interface EditValues {
	firstName: string
	lastName: string
	licensesAllotted: number
	licensesUsed: number
	userRole: string
}

const StatusChip = ({ status }: { status: UserRow['userStatus'] }) => {
	if (status === 'Account Created') {
		return (
			<Chip
				icon={<CheckIcon sx={styles.statusChipAccountCreatedIcon} />}
				label="Account Created"
				variant="outlined"
				size="small"
				sx={styles.statusChipAccountCreated}
			/>
		)
	}
	if (status === 'Invite Expired') {
		return (
			<Chip
				icon={<ErrorIcon sx={styles.statusChipInviteExpiredIcon} />}
				label="Invite Expired"
				variant="outlined"
				size="small"
				sx={styles.statusChipInviteExpired}
			/>
		)
	}
	return (
		<Chip
			icon={<ErrorIcon sx={styles.statusChipSetupPendingIcon} />}
			label="Setup Pending"
			variant="outlined"
			size="small"
			sx={styles.statusChipSetupPending}
		/>
	)
}

const AdminPage = () => {
	const { showModal, store } = useModalContext()
	const [isAdminPageAuthorized, setIsAdminPageAuthorized] = useState(false)
	const [rows, setRows] = useState<UserRow[]>(adminDummyData)
	const [editRowId, setEditRowId] = useState<number | null>(null)
	const [editValues, setEditValues] = useState<EditValues | null>(null)

	useEffect(() => {
		if (
			isAdminPageAuthorized ||
			store.modalType === MODAL_TYPES.ADMIN_PAGE_ACCESS_MODAL
		) {
			return
		}

		showModal(MODAL_TYPES.ADMIN_PAGE_ACCESS_MODAL, {
			onAuthorized: () => setIsAdminPageAuthorized(true),
			disableBackdropClose: true,
			backdropBlur: 55,
		})
	}, [isAdminPageAuthorized, showModal, store.modalType])

	const isEditValid =
		editValues !== null &&
		editValues.firstName.trim().length >= 1 &&
		editValues.lastName.trim().length >= 1 &&
		USER_ROLES.includes(
			editValues.userRole as (typeof USER_ROLES)[number]
		) &&
		Number.isInteger(editValues.licensesUsed) &&
		Number.isInteger(editValues.licensesAllotted) &&
		editValues.licensesUsed >= 0 &&
		editValues.licensesAllotted >= 0 &&
		editValues.licensesUsed <= editValues.licensesAllotted

	const handleEditClick = (row: UserRow) => {
		setEditRowId(row.id)
		setEditValues({
			firstName: row.firstName,
			lastName: row.lastName,
			userRole: row.userRole,
			licensesUsed: row.licensesUsed,
			licensesAllotted: row.licensesAllotted,
		})
	}

	const handleCancelEdit = () => {
		setEditRowId(null)
		setEditValues(null)
	}

	const handleSaveEdit = () => {
		if (!editValues) return
		// Future: replace setRows with await apiCall(editValues), then setRows on success
		setRows((prev) =>
			prev.map((r) => (r.id === editRowId ? { ...r, ...editValues } : r))
		)
		setEditRowId(null)
		setEditValues(null)
	}

	const handleDeleteClick = (userEmail: string) => {
		showModal(MODAL_TYPES.REMOVE_LICENSE_MODAL, { userEmail })
	}

	const columns: GridColDef[] = [
		{
			field: 'checkbox',
			headerName: '',
			width: 50,
			sortable: false,
			renderCell: () => <Checkbox size="medium" />,
		},
		{ field: 'company', headerName: 'Company', flex: 1, minWidth: 140 },
		{
			field: 'licenses',
			headerName: 'Licenses',
			flex: 1,
			minWidth: 120,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserRow
				if (row.id === editRowId && editValues) {
					return (
						<Box sx={styles.licensesCell}>
							<TextField
								variant="standard"
								value={editValues.licensesUsed}
								inputProps={{ inputMode: 'numeric', min: 0 }}
								sx={styles.licensesEditField}
								onChange={(e) => {
									const val = parseInt(e.target.value, 10)
									setEditValues((prev) =>
										prev
											? {
													...prev,
													licensesUsed: isNaN(val)
														? 0
														: val,
												}
											: prev
									)
								}}
							/>
							<span>/</span>
							<TextField
								variant="standard"
								value={editValues.licensesAllotted}
								inputProps={{
									inputMode: 'numeric',
									min: 0,
									'data-testid':
										'admin-edit-licenses-allotted-input',
								}}
								sx={styles.licensesEditField}
								onChange={(e) => {
									const val = parseInt(e.target.value, 10)
									setEditValues((prev) =>
										prev
											? {
													...prev,
													licensesAllotted: isNaN(val)
														? 0
														: val,
												}
											: prev
									)
								}}
							/>
						</Box>
					)
				}
				return (
					<span>{`${row.licensesUsed}/${row.licensesAllotted}`}</span>
				)
			},
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			flex: 1,
			minWidth: 120,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserRow
				if (row.id === editRowId && editValues) {
					return (
						<TextField
							variant="standard"
							value={editValues.firstName}
							inputProps={{
								'data-testid': 'admin-edit-first-name-input',
							}}
							sx={styles.editTextField}
							onChange={(e) =>
								setEditValues((prev) =>
									prev
										? { ...prev, firstName: e.target.value }
										: prev
								)
							}
						/>
					)
				}
				return <span>{row.firstName}</span>
			},
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			flex: 1,
			minWidth: 120,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserRow
				if (row.id === editRowId && editValues) {
					return (
						<TextField
							variant="standard"
							value={editValues.lastName}
							inputProps={{
								'data-testid': 'admin-edit-last-name-input',
							}}
							sx={styles.editTextField}
							onChange={(e) =>
								setEditValues((prev) =>
									prev
										? { ...prev, lastName: e.target.value }
										: prev
								)
							}
						/>
					)
				}
				return <span>{row.lastName}</span>
			},
		},
		{
			field: 'userEmail',
			headerName: 'User Email',
			flex: 1,
			minWidth: 160,
			renderCell: (params: GridRenderCellParams) => (
				<Box
					component="a"
					href={`mailto:${params.value}`}
					sx={styles.emailLink}
				>
					{params.value}
				</Box>
			),
		},
		{
			field: 'userStatus',
			headerName: 'User Status',
			flex: 1,
			minWidth: 160,
			renderCell: (params: GridRenderCellParams) => (
				<StatusChip status={params.value as UserRow['userStatus']} />
			),
		},
		{
			field: 'userRole',
			headerName: 'User Role',
			flex: 1,
			minWidth: 140,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserRow
				if (row.id === editRowId && editValues) {
					return (
						<Select
							variant="standard"
							value={editValues.userRole}
							sx={styles.editSelect}
							MenuProps={{ disableScrollLock: true }}
							onChange={(e: SelectChangeEvent) =>
								setEditValues((prev) =>
									prev
										? { ...prev, userRole: e.target.value }
										: prev
								)
							}
						>
							{USER_ROLES.map((role) => (
								<MenuItem
									key={role}
									value={role}
									sx={styles.editSelectMenuItem}
								>
									{role}
								</MenuItem>
							))}
						</Select>
					)
				}
				return <span>{row.userRole}</span>
			},
		},
		{
			field: 'licenseExpiration',
			headerName: 'License Expiration',
			flex: 1,
			minWidth: 150,
		},
		{
			field: 'actions',
			headerName: 'Change Details',
			width: 160,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserRow
				if (row.id === editRowId) {
					return (
						<Box sx={styles.cancelSaveCell}>
							<Button
								data-testid="admin-edit-cancel-button"
								size="small"
								variant="text"
								onClick={handleCancelEdit}
							>
								CANCEL
							</Button>
							<Button
								data-testid="admin-edit-save-button"
								size="small"
								variant="contained"
								color="secondary"
								disabled={!isEditValid}
								onClick={handleSaveEdit}
							>
								SAVE
							</Button>
						</Box>
					)
				}
				return (
					<Box sx={styles.actionsCell}>
						<IconButton
							size="medium"
							sx={styles.actionsCellIcon}
							onClick={() => handleEditClick(row)}
						>
							<EditIcon fontSize="medium" />
						</IconButton>
						<IconButton
							size="medium"
							sx={styles.actionsCellIcon}
							onClick={() => handleDeleteClick(row.userEmail)}
						>
							<DeleteIcon fontSize="medium" />
						</IconButton>
					</Box>
				)
			},
		},
	]

	if (!isAdminPageAuthorized) {
		return <Stack data-testid={TEST_ID} sx={styles.StackContainer} />
	}

	return (
		<Stack data-testid={TEST_ID} sx={styles.StackContainer}>
			<SubPageHeader
				title="User Management"
				subtitle="Manage Brainstorm teams and users here"
				backgroundGradient="linear-gradient(90deg, #9DCDF2 0%, #BDDEF6 100%)"
			/>

			<Box sx={styles.ContentBox}>
				<Container maxWidth={'xl'} sx={{ ...styles.list }}>
					<Box sx={styles.SearchRow}>
						<OutlinedInput
							startAdornment={
								<InputAdornment position="start">
									<IconButton>
										<Search />
									</IconButton>
								</InputAdornment>
							}
							placeholder="Search..."
							inputProps={{ 'data-testid': 'admin-search-input' }}
							sx={styles.searchBarStyle}
						/>
						<Button
							data-testid="admin-add-new-users-button"
							color="secondary"
							variant="outlined"
							size="medium"
							sx={styles.addNewUsersButton}
						>
							Add New Users
						</Button>
					</Box>

					<Box sx={styles.dataGridWrapper}>
						<DataGrid
							rows={rows}
							columns={columns}
							initialState={{
								pagination: {
									paginationModel: { pageSize: 10 },
								},
							}}
							pageSizeOptions={[10, 25, 50]}
							disableRowSelectionOnClick
							sx={styles.dataGrid}
						/>
					</Box>
				</Container>
			</Box>
		</Stack>
	)
}

export default AdminPage
